import React from 'react';

import SubjectRoundedIcon from '@mui/icons-material/SubjectRounded';
import {
  CardContent, Typography, useMediaQuery, useTheme,
} from '@mui/material';

import { BaseColorKeys, SubjectColorKeys } from '../../theme';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ArrowIcon,
  CardContainer,
  NoteIndex,
  NoteLabel,
  NotesList,
  NotesListEntry,
  TitleContainer,
  TitleDivider,
  TitleIcon,
  TitleStack,
} from './index.styles';

export interface NotesEntry {
  label: string,
  shortLabel?: string,
  key: string,
  href?: string,
  isExternalLink?: boolean,
}

interface NotesCategoryCardProps {
  // Heading label for the category
  categoryLabel: string,
  // Runs when a category is clicked
  notes?: NotesEntry[],
  // Color theme for the card
  color?: BaseColorKeys | SubjectColorKeys,
  onNoteClick?: (note: NotesEntry) => void,
}

const NotesCategoryCard = ({
  categoryLabel,
  notes = [],
  color = 'primary',
  onNoteClick = () => {},
}: NotesCategoryCardProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const [expanded, setExpanded] = React.useState<boolean>(false);

  return (
    <CardContainer color={color}>
      <CardContent>
        <Accordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
          disableGutters
          elevation={0}
          square
        >
          <AccordionSummary
            expandIcon={(
              <ArrowIcon $expanded={expanded} />
            )}
          >
            <TitleContainer>
              <TitleIcon color={color}>
                <SubjectRoundedIcon />
              </TitleIcon>
              <TitleStack>
                <Typography variant="h5" component="div">
                  {categoryLabel.split(':')[0]}
                </Typography>
                {categoryLabel.split(':').length > 1 && (
                  <Typography variant="caption" component="div">
                    {categoryLabel.split(':')[1]}
                  </Typography>
                )}
              </TitleStack>
            </TitleContainer>
          </AccordionSummary>
          <TitleDivider />
          <AccordionDetails>
            {notes.length > 0 ? (
              <NotesList isSingleColumn={!isMd}>
                {(notes || []).map((note, index) => (
                  <NotesListEntry key={note.label}>
                    <NoteIndex variant="body2">
                      {index + 1}
                    </NoteIndex>
                    <NoteLabel
                      variant="body2"
                      onClick={() => onNoteClick(note)}
                    >
                      {note.label}
                    </NoteLabel>
                  </NotesListEntry>
                ))}
              </NotesList>
            ) : (
              <Typography variant="caption">
                There are no notes available under this category yet.
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </CardContainer>
  );
};

export default NotesCategoryCard;
